import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/react-hooks';
import { VariableSizeList as List } from 'react-window';
import { Skeleton } from '@mui/material';

import './mobile-shop-tab-style.scss';
import useIsComDomain from '@src/hooks/useIsComDomain';
import useBrandList from '@src/hooks/useBrandList';

import Button from '@src/componentsNew/Buttons/Button';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@src/componentsNew/Typography/Typography';

import TabBar from '@src/componentsNew/TabBar';
import MobileSearchInput from '@src/componentsNew/Inputs/MobileSearchInput';
import OfferList from '@src/componentsNew/Offers/OfferList';

import { MENU, UI_COMPONENT } from '@queries';
import { Menu } from '@src/queries/__generated__/Menu';
import { uiComponent } from '@src/queries/__generated__/uiComponent';
import { uiComponentVariables } from '@src/core/config';
import { isNew } from '@src/utils';
import { goToUrl } from '@src/utils/urlCheck';
import { SKELETON_COLOR } from '@src/utils/constants';
import { getInvisibleBrandIds } from "@src/utils/constants";

function MobileShopTabPage() {
  const history = useHistory();
  const isComDomain = useIsComDomain();
  const { groupedBrandList, fetchBrandListLoading } = useBrandList();

  const [menuItems, setMenuItems] = useState([]);
  const [tabItems, setTabItems] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAlphabet, setSelectedAlphabet] = useState(null);

  const brandListRef = useRef(null);
  const [listHeight, setListHeight] = useState(window.innerHeight * 2);

  const { data } = useQuery<Menu>(MENU, {
    variables: { name: 'navbar' },
    onCompleted: (data) => {
      setMenuItems(JSON.parse(data.menu.jsonContent));
    },
  });

  const [getTopBrandData, { data: topBrandData, loading: topBrandLoading }] = useLazyQuery<uiComponent>(UI_COMPONENT, {
    fetchPolicy: 'no-cache',
  });

  const activeTopBrands = topBrandData?.uicomponent?.items?.filter((item) => item.isActive) ?? [];

  const filteredTabItems = (menuItems) => {
    return menuItems.filter((item) => {
      const itemName = item.name.toLowerCase();
      return ['categories', 'brands', 'offers'].includes(itemName);
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setListHeight(window.innerHeight * 2);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const tabItem = filteredTabItems(menuItems);
    setTabItems(tabItem);
  }, [menuItems]);

  useEffect(() => {
    if (selectedAlphabet !== null && brandListRef.current) {
      const index = Object.keys(groupedBrandList ?? {}).indexOf(selectedAlphabet);
      if (index !== -1) {
        const count = Object.keys(groupedBrandList).length;
        brandListRef.current.scrollToItem(Math.min(index, count - 1), 'start');
      }
    }
  }, [selectedAlphabet, groupedBrandList]);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);

    // brand 탭 클릭 시
    if (newValue === 1 && !topBrandData) {
      getTopBrandData({
        variables: isComDomain ? uiComponentVariables.topBrandWebCom : uiComponentVariables.topBrandWeb,
      });
    }
  };

  const handleClickAlphabet = (e, alphabet: string) => {
    e.stopPropagation();

    setSelectedAlphabet(alphabet);
  };

  // Dynamically calculate the height of each item
  const getBrandListElementSize = useCallback(
    (index) => {
      const alphabet = Object.keys(groupedBrandList)[index];

      const alphabetHeaderHeight = 32; // Base height for the alphabet header
      const brandItemHeight = 49.19; // Height for each brand item
      const brandsCountStartWithAlphabet = groupedBrandList[alphabet]?.length || 0;

      return alphabetHeaderHeight + brandsCountStartWithAlphabet * brandItemHeight;
    },
    [groupedBrandList],
  );

  // Render each brand item
  const renderBrandItem = ({ index, style }) => {
    const alphabet = Object.keys(groupedBrandList)[index];

    return (
      <div style={style} id={alphabet === '#' ? 'notAlphabetGroup' : alphabet} className="alphabet-sorted-brand-list">
        <Typography variant="body1" component="span" style={{ color: '#F82296' }}>
          {alphabet}
        </Typography>

        <ul className="brand-item-wrapper">
          {groupedBrandList[alphabet]?.filter((item) => !getInvisibleBrandIds().includes(String(item.id)))
            .map((item) => (
            <li key={item?.name} className="brand-item" onClick={() => goToUrl(history, item?.url)}>
              <Typography variant="body3" component="span">
                {item?.name}
              </Typography>

              {isNew(item?.publication_date) && (
                <Typography variant="body2" component="span" style={{ color: '#EF6BFF' }}>
                  new
                </Typography>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <nav id="ShopTabWrapper">
      <div className="fixed-area">
        <div className="search-input-wrapper">
          <MobileSearchInput isSearchPage={false} />
        </div>
        <TabBar tabItems={tabItems} selectedTab={selectedTab} onTabChange={handleTabChange} />
      </div>

      <section className="tab-items-area">
        {tabItems[selectedTab]?.name === 'CATEGORIES' &&
          tabItems[selectedTab]?.child_items.map((level1, idx) => (
            <Accordion className="category-accordion" key={idx} square={true}>
              <AccordionSummary
                className="category-level1"
                expandIcon={level1.child_items.length ? <ExpandMoreIcon /> : null}
              >
                <Typography variant="body3" component="span">
                  {level1.name}
                </Typography>
              </AccordionSummary>
              {level1.child_items.map((level2, j) => (
                <AccordionSummary key={j} className="category-level2" onClick={() => goToUrl(history, level2.url)}>
                  <Typography variant="body3" component="span">
                    {level2.name}
                  </Typography>
                </AccordionSummary>
              ))}
            </Accordion>
          ))}

        {tabItems[selectedTab]?.name === 'BRANDS' && (
          <>
            {topBrandLoading && (
              <div className="top-brands-skeleton-wrapper">
                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={20}
                  style={{ backgroundColor: SKELETON_COLOR, borderRadius: 6 }}
                />

                <div className="top-brands-skeleton-image-container">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Skeleton
                      key={index}
                      className="skeleton-image"
                      variant="circular"
                      width={80}
                      height={80}
                      style={{ backgroundColor: SKELETON_COLOR }}
                    />
                  ))}
                </div>
              </div>
            )}

            {!topBrandLoading && activeTopBrands.length > 0 && (
              <div className="top-brands-wrapper">
                <Typography cName="top-brands-title" variant="subtitle1" component="span" style={{ color: '#212121' }}>
                  Top Rated Brands
                </Typography>
                <div className="top-brands-image-container">
                  {activeTopBrands.map((topBrand) => (
                    <img
                      key={topBrand.id}
                      className="top-brand-image"
                      src={topBrand.imageMobileUrl}
                      onClick={() => (window.location.href = topBrand.link)}
                      alt="top-brand-image"
                    />
                  ))}
                </div>
              </div>
            )}

            {!fetchBrandListLoading && (
              <div className="alphabet-sorted-brand-list-area">
                {/* Navigation button */}
                <div className="navigation-alphabet-wrapper">
                  {['#', ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))].map(
                    (alphabet) =>
                      groupedBrandList?.[alphabet] && (
                        <Button
                          key={alphabet}
                          size="medium"
                          cName={`navigation-alphabet-btn ${selectedAlphabet === alphabet ? 'selected' : ''}`}
                          onClick={(e) => handleClickAlphabet(e, alphabet)}
                        >
                          {alphabet}
                        </Button>
                      ),
                  )}
                </div>

                <Typography cName="top-brands-title" variant="subtitle1" component="span" style={{ color: '#212121' }}>
                  All Brands
                </Typography>

                {/* BrandList */}
                <List
                  ref={brandListRef}
                  height={listHeight}
                  itemCount={groupedBrandList ? Object.keys(groupedBrandList).length : 0} // Number of alphabet groups
                  itemSize={getBrandListElementSize} // Function to get the size of each item
                  width="100%"
                >
                  {renderBrandItem}
                </List>
              </div>
            )}
          </>
        )}

        {tabItems[selectedTab]?.name === 'OFFERS' && (
          <OfferList
            isFindMoreOffers={false}
            uiComponentVariable={isComDomain ? uiComponentVariables.mainOffersCom : uiComponentVariables.mainOffers}
          />
        )}
      </section>
    </nav>
  );
}

export default MobileShopTabPage;
